import { MutationFunctionOptions } from '@apollo/client';

import {
  CreateInternationalTransferQuoteInput,
  CreateInternationalTransferQuoteResponseError,
  CreateInternationalTransferQuoteResponseSuccess,
  Exact,
} from '__generated__/GQL';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation/useErrorHandledMutation';

import {
  CreateInternationalTransferQuoteDocument,
  CreateInternationalTransferQuoteMutation,
  CreateInternationalTransferQuoteMutationVariables,
} from './graphql/quoteCreation.gql';

const isResponseSuccess = (
  obj: CreateInternationalTransferQuoteMutation['createInternationalTransferQuote'],
): obj is CreateInternationalTransferQuoteResponseSuccess => {
  if (!obj) {
    return false;
  }

  return obj.isSuccess;
};

const isResponseError = (
  obj: CreateInternationalTransferQuoteMutation['createInternationalTransferQuote'],
): obj is CreateInternationalTransferQuoteResponseError => {
  if (!obj) {
    return false;
  }

  return !obj.isSuccess;
};

const getQuoteAndErrorsFromData = (
  data: CreateInternationalTransferQuoteMutation | null | undefined,
) => {
  if (!data) {
    return {
      errors: null,
      quote: null,
    };
  }

  const quote = isResponseSuccess(data.createInternationalTransferQuote)
    ? data.createInternationalTransferQuote.quote
    : null;

  const errors = isResponseError(data.createInternationalTransferQuote)
    ? data.createInternationalTransferQuote.errors
    : null;

  return {
    errors,
    quote,
  };
};

export const useCreateInternationalTransferQuote = () => {
  const [mutate, { data, loading: isCreatingQuote }] = useErrorHandledMutation<
    CreateInternationalTransferQuoteMutation,
    CreateInternationalTransferQuoteMutationVariables
  >(CreateInternationalTransferQuoteDocument);

  const createQuote = async (
    options: MutationFunctionOptions<
      CreateInternationalTransferQuoteMutation,
      Exact<{
        lang: string;
        input: CreateInternationalTransferQuoteInput;
      }>
    >,
  ) => {
    const { data: mutateData } = await mutate(options);

    return {
      isCreatingQuote: false,
      ...getQuoteAndErrorsFromData(mutateData),
    };
  };

  return {
    createQuote,
    isCreatingQuote,
    ...getQuoteAndErrorsFromData(data),
  };
};

export type UseInternationalTransferQuoteReturnType = ReturnType<
  typeof useCreateInternationalTransferQuote
>;
